import {
	BrokerProjectsTabValue,
	ConstructionSiteList,
	GFAFullDetails,
	PAGES,
} from "@dune/types";
import {
	Button,
	Checkbox,
	Combobox,
	DataTable,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	PaginatedDataTable,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	useDebounce,
	useSidebar,
} from "@dune/ui";
import { ColumnDef } from "@tanstack/react-table";
import { getMOAName, isBrokerProjectsTab } from "@dune/utils";
import { Building02, SearchSm } from "@untitled-ui/icons-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useReassignBrokerProjects } from "../../../../../api/mutations/societies.mutations";
import {
	getConstructionSiteIds,
	useGetConstructionSites,
} from "../../../../../api/queries/constructionSite.queries";
import { useGetGFAConstructionSites } from "../../../../../api/queries/gfa.queries";
import { useGetAllBrokersSocieties } from "../../../../../api/queries/societies.queries";
import { useMe } from "../../../../../api/queries/users.queries";
import {
	getConstructionSitesColumns,
	getGfaColumns,
} from "../../../../constructionSites/constructionList/constructionSitesColumns";
import { usePagination } from "../../../../../hooks/usePagination";
import { isGFAConstructionSite } from "../../../../../utils/gfa/gfa.utils";
import { RoleEnum } from "../../../../../../../../packages/vars/src";

const HIDE_BROKER_COLUMN = true;

type BrokerProjectsTabProps = {
	brokerSocietyId: number;
};

type SelectedProjectSummary = {
	reference: string;
	clientName: string;
};

const getClientNameLabel = (
	clientName: string | string[] | null | undefined,
) => {
	if (Array.isArray(clientName)) {
		return clientName.join(" ");
	}
	return clientName || "Client inconnu";
};

const BrokerProjectsTab: React.FC<BrokerProjectsTabProps> = ({
	brokerSocietyId,
}) => {
	const navigate = useNavigate();
	const { me } = useMe();
	const isDuneUser = me?.role === RoleEnum.DUNE;
	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, 1000);
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";
	const [activeTab, setActiveTab] =
		useState<BrokerProjectsTabValue>("constructionSite");
	const [selectedConstructionSiteIds, setSelectedConstructionSiteIds] =
		useState<number[]>([]);
	const [selectedProjectSummaries, setSelectedProjectSummaries] = useState<
		Record<number, SelectedProjectSummary>
	>({});
	const [isReassignDialogOpen, setIsReassignDialogOpen] = useState(false);
	const [isReassignConfirmStep, setIsReassignConfirmStep] = useState(false);
	const [selectedBrokerForReassign, setSelectedBrokerForReassign] =
		useState("");
	const [searchBrokerForReassign, setSearchBrokerForReassign] = useState("");
	const debouncedSearchBrokerForReassign = useDebounce(
		searchBrokerForReassign,
		400,
	);
	const [isSelectingAllProjects, setIsSelectingAllProjects] = useState(false);
	const { page, limit, handlePageChange, handleLimitChange } = usePagination();
	const { mutate: reassignBrokerProjects, isPending: isReassignPending } =
		useReassignBrokerProjects(brokerSocietyId);

	const {
		data: constructionSitesResponse = {
			data: [],
			meta: {
				page: 1,
				limit: 10,
				total: 0,
				totalPages: 0,
				hasNextPage: false,
				hasPreviousPage: false,
			},
		},
		isLoading,
		error,
	} = useGetConstructionSites(debouncedSearch, page, limit, brokerSocietyId);

	const {
		data: gfaConstructionSites = [],
		isLoading: isLoadingGfas,
		error: errorGfas,
	} = useGetGFAConstructionSites(debouncedSearch, brokerSocietyId);
	const { data: brokersResponse } = useGetAllBrokersSocieties(
		{ page: 1, limit: 50, search: debouncedSearchBrokerForReassign },
		{ enabled: isReassignDialogOpen },
	);

	const constructionSites = constructionSitesResponse.data;
	const paginationMeta = constructionSitesResponse.meta;
	const visibleConstructionSiteIds = useMemo(
		() => constructionSites.map((constructionSite) => constructionSite.id),
		[constructionSites],
	);
	const allVisibleProjectsSelected =
		visibleConstructionSiteIds.length > 0 &&
		visibleConstructionSiteIds.every((constructionSiteId) =>
			selectedConstructionSiteIds.includes(constructionSiteId),
		);
	const someVisibleProjectsSelected =
		!allVisibleProjectsSelected &&
		visibleConstructionSiteIds.some((constructionSiteId) =>
			selectedConstructionSiteIds.includes(constructionSiteId),
		);
	const selectedProjectsForModal = selectedConstructionSiteIds.map(
		(constructionSiteId) => ({
			id: constructionSiteId,
			summary: selectedProjectSummaries[constructionSiteId],
		}),
	);
	const selectedProjectsPreview = selectedProjectsForModal.slice(0, 50);
	const remainingSelectedProjectsCount = Math.max(
		selectedProjectsForModal.length - selectedProjectsPreview.length,
		0,
	);
	const brokersForReassign =
		brokersResponse?.data.filter((broker) => broker.id !== brokerSocietyId) ??
		[];

	const getProjectSummaryFromCurrentPage = useCallback(
		(constructionSiteId: number): SelectedProjectSummary | null => {
			const project = constructionSites.find(
				(site) => site.id === constructionSiteId,
			);
			if (!project) {
				return null;
			}
			return {
				reference: `CH-${project.id.toString().padStart(5, "0")}`,
				clientName: getClientNameLabel(
					getMOAName(project.constructionSiteClientDetails),
				),
			};
		},
		[constructionSites],
	);

	useEffect(() => {
		setSelectedProjectSummaries((previousSummaries) => {
			const missingIds = selectedConstructionSiteIds.filter(
				(constructionSiteId) => !previousSummaries[constructionSiteId],
			);
			if (missingIds.length === 0) {
				return previousSummaries;
			}

			const nextSummaries = { ...previousSummaries };
			let hasChanged = false;
			for (const constructionSiteId of missingIds) {
				const summary = getProjectSummaryFromCurrentPage(constructionSiteId);
				if (summary) {
					nextSummaries[constructionSiteId] = summary;
					hasChanged = true;
				}
			}

			return hasChanged ? nextSummaries : previousSummaries;
		});
	}, [selectedConstructionSiteIds, getProjectSummaryFromCurrentPage]);

	const setAllVisibleProjectsSelection = useCallback(
		(checked: boolean) => {
			if (checked) {
				setSelectedProjectSummaries((previousSummaries) => {
					const nextSummaries = { ...previousSummaries };
					for (const constructionSite of constructionSites) {
						nextSummaries[constructionSite.id] = {
							reference: `CH-${constructionSite.id.toString().padStart(5, "0")}`,
							clientName: getClientNameLabel(
								getMOAName(constructionSite.constructionSiteClientDetails),
							),
						};
					}
					return nextSummaries;
				});
				setSelectedConstructionSiteIds((previousSelection) =>
					Array.from(
						new Set([...previousSelection, ...visibleConstructionSiteIds]),
					),
				);
				return;
			}

			setSelectedConstructionSiteIds((previousSelection) =>
				previousSelection.filter(
					(constructionSiteId) =>
						!visibleConstructionSiteIds.includes(constructionSiteId),
				),
			);
			setSelectedProjectSummaries((previousSummaries) => {
				const nextSummaries = { ...previousSummaries };
				for (const constructionSiteId of visibleConstructionSiteIds) {
					delete nextSummaries[constructionSiteId];
				}
				return nextSummaries;
			});
		},
		[constructionSites, visibleConstructionSiteIds],
	);

	const toggleConstructionSiteSelection = useCallback(
		(constructionSiteId: number) => {
			setSelectedConstructionSiteIds((previousSelection) => {
				if (previousSelection.includes(constructionSiteId)) {
					setSelectedProjectSummaries((previousSummaries) => {
						const nextSummaries = { ...previousSummaries };
						delete nextSummaries[constructionSiteId];
						return nextSummaries;
					});
					return previousSelection.filter((id) => id !== constructionSiteId);
				}

				const summary = getProjectSummaryFromCurrentPage(constructionSiteId);
				if (summary) {
					setSelectedProjectSummaries((previousSummaries) => ({
						...previousSummaries,
						[constructionSiteId]: summary,
					}));
				}

				return [...previousSelection, constructionSiteId];
			});
		},
		[getProjectSummaryFromCurrentPage],
	);

	const handleSelectAllProjects = async () => {
		setIsSelectingAllProjects(true);
		try {
			const allFilteredConstructionSiteIds = await getConstructionSiteIds(
				debouncedSearch,
				brokerSocietyId,
			);
			setSelectedConstructionSiteIds(allFilteredConstructionSiteIds);
			setSelectedProjectSummaries((previousSummaries) => {
				const nextSummaries: Record<number, SelectedProjectSummary> = {};
				for (const constructionSiteId of allFilteredConstructionSiteIds) {
					const knownSummary = previousSummaries[constructionSiteId];
					if (knownSummary) {
						nextSummaries[constructionSiteId] = knownSummary;
					}
				}
				for (const constructionSite of constructionSites) {
					if (allFilteredConstructionSiteIds.includes(constructionSite.id)) {
						nextSummaries[constructionSite.id] = {
							reference: `CH-${constructionSite.id.toString().padStart(5, "0")}`,
							clientName: getClientNameLabel(
								getMOAName(constructionSite.constructionSiteClientDetails),
							),
						};
					}
				}
				return nextSummaries;
			});
		} catch {
			toast.error("Impossible de sélectionner tous les projets pour le moment");
		} finally {
			setIsSelectingAllProjects(false);
		}
	};

	const handleSelectNoProjects = () => {
		setSelectedConstructionSiteIds([]);
		setSelectedProjectSummaries({});
	};

	const handleOpenReassignDialog = () => {
		if (selectedConstructionSiteIds.length === 0) {
			toast.error("Veuillez sélectionner au moins un chantier");
			return;
		}
		setIsReassignDialogOpen(true);
	};

	const handleRequestReassignConfirm = () => {
		if (!selectedBrokerForReassign) {
			toast.error("Veuillez sélectionner un apporteur");
			return;
		}
		setIsReassignConfirmStep(true);
	};

	const handleConfirmReassign = () => {
		reassignBrokerProjects(
			{
				newBrokerSocietyId: Number(selectedBrokerForReassign),
				constructionSiteIds: selectedConstructionSiteIds,
			},
			{
				onSuccess: () => {
					setIsReassignDialogOpen(false);
					setIsReassignConfirmStep(false);
					setSelectedBrokerForReassign("");
					setSelectedConstructionSiteIds([]);
					setSelectedProjectSummaries({});
				},
			},
		);
	};

	const constructionSiteColumns = useMemo<
		ColumnDef<ConstructionSiteList | GFAFullDetails>[]
	>(() => {
		const baseColumns = getConstructionSitesColumns(HIDE_BROKER_COLUMN);
		if (!isDuneUser) return baseColumns;

		const selectionColumn: ColumnDef<ConstructionSiteList | GFAFullDetails> = {
			id: "selection",
			header: () => (
				<Checkbox
					checked={
						allVisibleProjectsSelected
							? true
							: someVisibleProjectsSelected
								? "indeterminate"
								: false
					}
					onCheckedChange={(checked) =>
						setAllVisibleProjectsSelection(Boolean(checked))
					}
					aria-label="Sélectionner tous les projets visibles"
				/>
			),
			cell: ({ row }) => (
				<div className="w-full h-full flex items-center justify-center">
					<Checkbox
						checked={selectedConstructionSiteIds.includes(row.original.id)}
						onCheckedChange={() =>
							toggleConstructionSiteSelection(row.original.id)
						}
						aria-label={`Sélectionner le chantier ${row.original.id}`}
					/>
				</div>
			),
			enableSorting: false,
		};

		return [selectionColumn, ...baseColumns];
	}, [
		isDuneUser,
		allVisibleProjectsSelected,
		someVisibleProjectsSelected,
		setAllVisibleProjectsSelection,
		selectedConstructionSiteIds,
		toggleConstructionSiteSelection,
	]);

	if (error || errorGfas) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-red-600">
				Erreur lors du chargement des projets.
			</div>
		);
	}

	return (
		<div className="space-y-4 flex-1 min-h-0 flex flex-col w-full min-w-0">
			<div className="flex flex-row gap-2 w-full max-w-md">
				<Input
					type="text"
					placeholder="Recherche rapide"
					value={searchInput}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setSearchInput(e.target.value)
					}
					iconOrText={<SearchSm />}
					iconOrTextPosition="right"
				/>
			</div>
		{activeTab === "constructionSite" && isDuneUser && (
			<div className="flex flex-wrap items-center gap-2">
				<Button variant="outline" onClick={handleSelectNoProjects}>
					Aucun
				</Button>
				<Button variant="outline" onClick={handleSelectAllProjects}>
					{isSelectingAllProjects ? "Sélection..." : "Tous"}
				</Button>
				<Button
					onClick={handleOpenReassignDialog}
					disabled={selectedConstructionSiteIds.length === 0}
				>
					Changer l'apporteur ({selectedConstructionSiteIds.length})
				</Button>
				<span className="text-sm text-muted-foreground">
					La sélection est conservée entre les pages
				</span>
			</div>
		)}
			<Tabs
				value={activeTab}
				onValueChange={(value) => {
					if (isBrokerProjectsTab(value)) {
						setActiveTab(value);
					}
				}}
				className="flex-1 min-h-0 flex flex-col w-full min-w-0"
			>
				<TabsList>
					<TabsTrigger value="constructionSite" variant="compact">
						Chantier de construction
					</TabsTrigger>
					<TabsTrigger value="gfa" variant="compact">
						GFA
					</TabsTrigger>
				</TabsList>
				<TabsContent
					value="constructionSite"
					className="mt-4 flex-1 min-h-0 flex flex-col w-full min-w-0"
				>
					<div className="flex-1 min-h-0 w-full min-w-0 overflow-auto">
						<div
							className={
								isCollapsed ? "w-full max-w-[92vw]" : "w-full max-w-[80vw]"
							}
						>
							<PaginatedDataTable
								columns={constructionSiteColumns}
								data={constructionSites}
								meta={paginationMeta}
								onPageChange={handlePageChange}
								onLimitChange={handleLimitChange}
								isLoading={isLoading}
								noResultsText="Aucun chantier trouvé"
								icon={Building02}
								variant="default-with-sort"
								onRowClick={(constructionSite) => {
									if (isGFAConstructionSite(constructionSite)) {
										navigate(
											PAGES.GFA.show.withParams(constructionSite.id.toString()),
										);
									} else {
										navigate(
											PAGES.PROJECTS.show.withParams(
												constructionSite.id.toString(),
											),
										);
									}
								}}
							/>
						</div>
					</div>
				</TabsContent>
				<TabsContent
					value="gfa"
					className="mt-4 flex-1 min-h-0 flex flex-col w-full min-w-0"
				>
					<div className="flex-1 min-h-0 w-full min-w-0 overflow-auto">
						<div
							className={
								isCollapsed ? "w-full max-w-[92vw]" : "w-full max-w-[80vw]"
							}
						>
							<DataTable
								columns={getGfaColumns(HIDE_BROKER_COLUMN)}
								data={gfaConstructionSites}
								isLoading={isLoadingGfas}
								noResultsText="Aucun GFA trouvé"
								icon={Building02}
								variant="default-with-sort"
								onRowClick={(constructionSite) => {
									if (isGFAConstructionSite(constructionSite)) {
										navigate(
											PAGES.GFA.show.withParams(constructionSite.id.toString()),
										);
									} else {
										navigate(
											PAGES.PROJECTS.show.withParams(
												constructionSite.id.toString(),
											),
										);
									}
								}}
							/>
						</div>
					</div>
				</TabsContent>
			</Tabs>
		{isDuneUser && <Dialog
			open={isReassignDialogOpen}
			onOpenChange={(nextOpen) => {
				if (!nextOpen) {
					setSelectedBrokerForReassign("");
					setSearchBrokerForReassign("");
					setIsReassignConfirmStep(false);
				}
				setIsReassignDialogOpen(nextOpen);
			}}
		>
			<DialogContent>
				{!isReassignConfirmStep ? (
					<>
						<DialogHeader>
							<DialogTitle>Changer l'apporteur</DialogTitle>
							<DialogDescription>
								Sélectionnez le nouvel apporteur à assigner aux{" "}
								{selectedConstructionSiteIds.length} projet(s) sélectionné(s).
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-2">
							<div className="rounded-md border p-3">
								<div className="text-sm font-medium mb-2">
									Projets sélectionnés ({selectedConstructionSiteIds.length})
								</div>
								<div className="max-h-40 overflow-auto space-y-1">
									{selectedProjectsForModal.map((selectedProject) => (
										<div
											key={selectedProject.id}
											className="text-sm text-muted-foreground"
										>
											{selectedProject.summary
												? `${selectedProject.summary.reference} - ${selectedProject.summary.clientName}`
												: `CH-${selectedProject.id.toString().padStart(5, "0")}`}
										</div>
									))}
									{remainingSelectedProjectsCount > 0 && (
										<div className="text-sm text-muted-foreground">
											... et {remainingSelectedProjectsCount} autre(s)
										</div>
									)}
								</div>
							</div>
							<Combobox
								triggerLabel="Sélectionner un apporteur"
								optionName="apporteur"
								options={brokersForReassign.map((broker) => ({
									label: broker.denomination,
									value: broker.id.toString(),
								}))}
								value={selectedBrokerForReassign}
								onValueChange={(value) =>
									setSelectedBrokerForReassign(
										value === selectedBrokerForReassign ? "" : value,
									)
								}
								onSearch={setSearchBrokerForReassign}
								disabled={false}
								disablePortal
							/>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setIsReassignDialogOpen(false)}
							>
								Annuler
							</Button>
							<Button
								onClick={handleRequestReassignConfirm}
								disabled={!selectedBrokerForReassign}
							>
								Valider
							</Button>
						</DialogFooter>
					</>
				) : (
					<>
						<DialogHeader>
							<DialogTitle className="text-destructive">
								Confirmer la réassignation
							</DialogTitle>
						</DialogHeader>
						<div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
							Vous êtes sur le point de réassigner{" "}
							<span className="font-semibold">
								{selectedConstructionSiteIds.length} projet(s)
							</span>{" "}
							à l'apporteur{" "}
							<span className="font-semibold">
								{brokersForReassign.find(
									(b) => b.id.toString() === selectedBrokerForReassign,
								)?.denomination}
							</span>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setIsReassignConfirmStep(false)}
								disabled={isReassignPending}
							>
								Retour
							</Button>
							<Button
								variant="destructive"
								onClick={handleConfirmReassign}
								disabled={isReassignPending}
								isLoading={isReassignPending}
							>
								Confirmer la réassignation
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>}
		</div>
	);
};

export default BrokerProjectsTab;
